import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CertificationCheck, OfficerCertificationSummary } from '@munifocus/contracts-gov';
import { api } from './api-client';

/**
 * Training credentials, read-only. Certificates are issued in the Admin Portal
 * after MuniFocus training; this portal only shows them, and warns when a
 * municipality that asked for a certification is assigning a file to an
 * officer without it.
 *
 * Keyed by language because course and session titles come back translated.
 */

const STALE_MS = 5 * 60 * 1000;

function useLang(): 'en' | 'fr' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('fr') ? 'fr' : 'en';
}

export const certificationKeys = {
  mine: (lang: string) => ['certifications', 'me', lang] as const,
  officer: (lgId: string, userId: string, lang: string) =>
    ['certifications', 'officer', lgId, userId, lang] as const,
  check: (lgId: string, officerId: string, lang: string) =>
    ['certifications', 'check', lgId, officerId, lang] as const,
};

/** The signed-in officer's own certifications. */
export function useMyCertifications() {
  const lang = useLang();
  return useQuery({
    queryKey: certificationKeys.mine(lang),
    queryFn: async () => {
      const { data } = await api.get<OfficerCertificationSummary>('/certifications/me', {
        params: { lang },
      });
      return data;
    },
    staleTime: STALE_MS,
  });
}

/** One roster member's certifications, from a municipality they work for. */
export function useOfficerCertifications(
  localGovernmentId: string | null | undefined,
  userId: string | null | undefined,
) {
  const lang = useLang();
  return useQuery({
    queryKey: certificationKeys.officer(localGovernmentId ?? '', userId ?? '', lang),
    queryFn: async () => {
      const { data } = await api.get<OfficerCertificationSummary>(
        `/local-governments/${localGovernmentId}/officers/${userId}/certifications`,
        { params: { lang } },
      );
      return data;
    },
    enabled: !!localGovernmentId && !!userId,
    staleTime: STALE_MS,
  });
}

/**
 * Whether assigning a file to this officer at this municipality deserves a
 * warning. Disabled until both are chosen; `enabled: false` in the answer
 * means the municipality has set no requirements (the default).
 */
export function useCertificationCheck(
  localGovernmentId: string | null | undefined,
  officerId: string | null | undefined,
) {
  const lang = useLang();
  return useQuery({
    queryKey: certificationKeys.check(localGovernmentId ?? '', officerId ?? '', lang),
    queryFn: async () => {
      const { data } = await api.get<CertificationCheck>(
        `/local-governments/${localGovernmentId}/certification-check`,
        { params: { officerId, lang } },
      );
      return data;
    },
    enabled: !!localGovernmentId && !!officerId,
    staleTime: STALE_MS,
  });
}
